import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
} from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'

export interface AppUser {
  id: string
  email: string | null
  user_metadata?: {
    full_name?: string | null
    name?: string | null
  }
}

interface AppSession {
  access_token: string
}

interface AuthContextType {
  user: AppUser | null
  session: AppSession | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signUp: (email: string, password: string) => Promise<{ error: any }>
  signInWithGoogle: () => Promise<{ error: any }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const isDashboardSubdomain = () => {
  const hostname = window.location.hostname
  return hostname.startsWith('dashboard.') || hostname === 'dashboard.localhost'
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null)
  const [session, setSession] = useState<AppSession | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const mapUser = async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser) {
        setUser(null)
        setSession(null)
        setLoading(false)
        return
      }

      const token = await firebaseUser.getIdToken()
      setUser({
        id: firebaseUser.uid,
        email: firebaseUser.email,
        user_metadata: {
          full_name: firebaseUser.displayName,
          name: firebaseUser.displayName,
        },
      })
      setSession({
        access_token: token,
      })
      setLoading(false)
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      mapUser(firebaseUser).catch((err) => {
        console.error('Error processing auth state:', err)
        setLoading(false)
      })
    })

    return () => unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password)
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const signUp = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password)
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider)

      if (isDashboardSubdomain()) {
        window.location.href = '/overview'
      } else {
        const hostname = window.location.hostname
        const protocol = window.location.protocol
        let dashboardHostname
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          dashboardHostname = 'dashboard.localhost'
        } else {
          const parts = hostname.split('.')
          if (parts.length >= 2) {
            dashboardHostname = 'dashboard.' + parts.slice(-2).join('.')
          } else {
            dashboardHostname = 'dashboard.' + hostname
          }
        }
        window.location.href = `${protocol}//${dashboardHostname}/overview`
      }

      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const signOut = async () => {
    await firebaseSignOut(auth)
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
